// Commissions → Adjustments: visible corrections (client credits, late costs,
// voided invoices, or entered by Finance). Nothing changes a payment silently —
// each adjustment waits for approval, then rides the next payout batch.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ADJ_SOURCE_LABEL, errReason, fmtDay, money, nameOr, usePlans } from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import { ConfirmAction, Empty, Field, INPUT, Loading, Notice, Pill, Section, TEXTAREA, T_HEAD, T_NUM, T_ROW, T_TD, T_TH, T_WRAP } from "./parts";

interface AdjustmentRow {
  id: string;
  rep_id: string;
  rep_name: string | null;
  invoice_id: string | null;
  invoice_number: number | string | null;
  amount: string;
  source: string;
  reason: string | null;
  status: "pending" | "approved" | "rejected" | string;
  created_at: string;
  decided_at: string | null;
  payout_batch_id: string | null;
  batch_status: string | null;
  /** Approved negative adjustment not deducted (Settings policy isn't 'deduct'). */
  held?: boolean;
}

export function AdjustmentsTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["commissions", "adjustments"],
    queryFn: () => api<{ rows: AdjustmentRow[] }>("/commissions/adjustments"),
  });
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string } | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const decide = useMutation({
    mutationFn: (a: { id: string; approve: boolean; reason?: string }) =>
      api(`/commissions/adjustments/${a.id}/decide`, { method: "POST", body: JSON.stringify({ approve: a.approve, reason: a.reason ?? null }) }),
    onSuccess: (_r, a) => {
      setMsg({ tone: "ok", text: a.approve ? "Adjustment approved." : "Adjustment rejected." });
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  return (
    <div>
      {msg && (
        <Notice tone={msg.tone} className="mb-5">
          {msg.text}
        </Notice>
      )}
      <Section
        title="Adjustments"
        intro="Corrections to commission already approved or paid. Approved adjustments go into the next payout batch. Negative adjustments are held — not deducted from a payout — unless Settings says to deduct them."
        action={
          !formOpen && (
            <Button onClick={() => setFormOpen(true)}>New adjustment</Button>
          )
        }
      >
        {formOpen && (
          <NewAdjustment
            onClose={() => setFormOpen(false)}
            onDone={(t) => {
              setMsg({ tone: "ok", text: t });
              setFormOpen(false);
            }}
          />
        )}
        {q.isError ? (
          <Notice tone="risk">{errReason(q.error)}</Notice>
        ) : !q.data ? (
          <Loading />
        ) : q.data.rows.length === 0 ? (
          <Empty>No adjustments yet.</Empty>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full min-w-[1100px]">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Why</th>
                  <th className={T_TH}>Invoice</th>
                  <th className={`${T_TH} text-right`}>Amount</th>
                  <th className={T_TH}>Status</th>
                  <th className={T_TH}>Recorded</th>
                  <th className={T_TH} />
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((a) => (
                  <tr key={a.id} className={T_ROW}>
                    <td className={`${T_TD} font-medium`}>{nameOr(a.rep_name)}</td>
                    <td className={T_TD}>
                      <span className="block">{a.reason ?? "—"}</span>
                      <span className="text-[14px] text-muted">{ADJ_SOURCE_LABEL[a.source] ?? a.source}</span>
                    </td>
                    <td className={`${T_TD} tabular-nums`}>{a.invoice_number ? `#${a.invoice_number}` : "—"}</td>
                    <td className={`${T_NUM} font-semibold ${Number(a.amount) < 0 ? "text-risk" : ""}`}>{money(a.amount)}</td>
                    <td className={T_TD}>
                      <AdjStatus a={a} />
                    </td>
                    <td className={`${T_TD} whitespace-nowrap`}>{fmtDay(a.created_at)}</td>
                    <td className={`${T_TD} whitespace-nowrap text-right`}>
                      {a.status === "pending" && (
                        <div className="flex flex-wrap justify-end gap-2">
                          <ConfirmAction
                            label="Approve"
                            confirmLabel={`Yes, approve ${money(a.amount)}`}
                            disabled={decide.isPending}
                            onConfirm={() => decide.mutate({ id: a.id, approve: true })}
                          />
                          <ConfirmAction
                            label="Reject"
                            variant="outline"
                            confirmLabel="Yes, reject"
                            needReason="Short reason (kept in the history)"
                            disabled={decide.isPending}
                            onConfirm={(reason) => decide.mutate({ id: a.id, approve: false, reason })}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function AdjStatus({ a }: { a: AdjustmentRow }) {
  if (a.status === "pending") return <Pill tone="warn">Waiting for approval</Pill>;
  if (a.status === "rejected") return <Pill tone="closed">Rejected</Pill>;
  if (a.batch_status === "paid") return <Pill tone="active">Paid</Pill>;
  if (a.payout_batch_id) return <Pill tone="active">Approved · in a batch</Pill>;
  if (a.held) return <Pill tone="warn">Approved · held (not deducted)</Pill>;
  return <Pill tone="active">Approved</Pill>;
}

function NewAdjustment({ onClose, onDone }: { onClose: () => void; onDone: (t: string) => void }) {
  const qc = useQueryClient();
  const plans = usePlans();
  const [repId, setRepId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const amountOk = /^-?\d{1,7}(\.\d{1,2})?$/.test(amount.trim()) && Number(amount) !== 0;

  const create = useMutation({
    mutationFn: () =>
      api("/commissions/adjustments", {
        method: "POST",
        body: JSON.stringify({ rep_id: repId, amount: Number(amount).toFixed(2), reason: reason.trim() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["commissions"] });
      onDone("Adjustment recorded — it waits for approval.");
    },
    onError: (e) => setErr(errReason(e)),
  });

  return (
    <div className="mb-6 rounded-md border border-rule bg-white p-5">
      <div className="grid gap-4 md:grid-cols-[minmax(0,280px)_200px_minmax(0,1fr)]">
        <Field label="Rep">
          <select className={INPUT} value={repId} onChange={(e) => setRepId(e.target.value)}>
            <option value="">Choose…</option>
            {(plans.data?.reps ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.full_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount ($)" hint="Use a minus sign to take money back, e.g. -125.00">
          <input className={INPUT} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </Field>
        <Field label="Why">
          <textarea className={TEXTAREA} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={() => create.mutate()} disabled={!repId || !amountOk || reason.trim().length < 2 || create.isPending}>
          Record adjustment
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {err && <span className="text-[15px] text-risk">{err}</span>}
      </div>
    </div>
  );
}
